import React, {
  ReactNode,
} from 'react';
import MuiPopover, {
  PopoverOrigin,
  PopoverProps as MuiPopoverProps,
} from '@mui/material/Popover';
import isNil from 'lodash/isNil';
import clsx from 'clsx';

type Parts = 'root' | 'paper';
type MuiPopoverOnClose = NonNullable<MuiPopoverProps['onClose']>;
export type PopoverCloseEvent = Parameters<MuiPopoverOnClose>[0];
export type PopoverCloseReason = Parameters<MuiPopoverOnClose>[1];

export type Props = Omit<MuiPopoverProps, 'open' | 'classes'> & {
  children?: ReactNode;
  classes?: Partial<Record<Parts, string>>;
  onClose?: (event?: PopoverCloseEvent, reason?: PopoverCloseReason) => void;
  disableEscapeKeyDown?: boolean;
};

const defaultOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

export default function Popover({
  children,
  onClose,
  anchorEl,
  disableEscapeKeyDown = true,
  disableScrollLock = true,
  disableAutoFocus = false,
  disableEnforceFocus = false,
  disableRestoreFocus = false,
  anchorOrigin = defaultOrigin,
  transformOrigin = defaultOrigin,
  classes,
  ...props
}: Props) {
  const handleClose = (
    event: object,
    reason: PopoverCloseReason,
  ) => {
    if (reason === 'escapeKeyDown' && disableEscapeKeyDown) {
      return;
    }
    onClose?.(event as PopoverCloseEvent, reason);
  };

  return (
    <MuiPopover
      {...props}
      open={!isNil(anchorEl)}
      onClose={handleClose as MuiPopoverProps['onClose']}
      disableScrollLock={disableScrollLock}
      disableAutoFocus={disableAutoFocus}
      disableEnforceFocus={disableEnforceFocus}
      disableRestoreFocus={disableRestoreFocus}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      classes={{
        root: classes?.root,
        paper: clsx(
          '!overflow-y-visible !overflow-x-visible',
          classes?.paper,
        ),
      }}
    >
      {children}
    </MuiPopover>
  );
}
